import { Formik, FormikHelpers } from 'formik';

import { AvailableOrganizer } from 'modules/organizers/domain/interfaces/available-organizer';
import { getSportTypeLabel } from 'modules/core/domain/constants/sport-type-labels';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { isEmptyAddress } from 'modules/core/helpers/is-empty-address';
import { Pages } from 'modules/navigation/domain/enums/pages';
import organizerFormValidator from 'modules/organizer/domain/validators/organizer.validator';
import { useCallback, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Background, backgrounds } from 'shared/constants/backgrounds';
import { usePreventNavigation } from 'shared/hooks';
import type { MessageDescriptor } from 'shared/i18n';
import { getAvailableTournamentTypes } from 'shared/utils';
import { UDFormLogoField } from 'ud-form/components/logo-field';
import UDFormInput from 'ud-form/components/input';
import UDInputPhone from 'ud-form/components/input/phone';
import UDFormTextarea from 'ud-form/components/input/textarea';
import UDFormSelect, { UDFormAddressSelect } from 'ud-form/components/select';
import UDButton from 'ud-ui/components/button';
import {
  mapAddressToDadataArea,
  mapDadataAreaToAddress,
} from 'ud-ui/components/dadata-address-autocomplete/infra/data-mappers/dadata-area.data-mapper';
import { OrganizerUpsertPayload } from 'modules/organizer/domain/interfaces/organizer-upsert-payload';

type SportTypeOption = {
  label: string;
  value: string;
};

type DadataAreaType = any; // Заменить на правильный тип, когда будет доступен

export interface OrganizerFormValues {
  name: string;
  sportType: SportTypeOption;
  email?: string;
  phone?: string;
  address?: DadataAreaType;
  description?: string;
  logo: {
    img: string | File;
    background: Background;
  }[];
}

type Translate = (descriptor: MessageDescriptor) => string;

const sportTypeToSelectOption = (sportType: string, translate: Translate): SportTypeOption => {
  const sportTypeKey = sportType.toLowerCase();
  const label = getSportTypeLabel(sportTypeKey);
  return {
    label: label ? translate(label) : sportTypeKey,
    value: sportTypeKey,
  };
};

const sportTypesToSelectOptions = (availableTournaments: AvailableOrganizer[], translate: Translate): SportTypeOption[] =>
  getAvailableTournamentTypes(availableTournaments).map((sportType) => sportTypeToSelectOption(sportType, translate));

type OrganizerFormProps = {
  isLoading?: boolean;
  initialValues?: OrganizerUpsertPayload & { backgroundColor?: Background };
  onSubmit: (values: OrganizerUpsertPayload, formikHelpers: FormikHelpers<OrganizerFormValues>) => void;
};

export const OrganizerForm = ({ isLoading, initialValues, onSubmit }: OrganizerFormProps) => {
  const { t } = useLingui();

  // TODO: add filter by available sport type
  const sportTypeOptions = sportTypesToSelectOptions([], t);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [leavingConfirmed, setLeavingConfirmed] = useState(false);

  const initialFormValues: OrganizerFormValues = initialValues
    ? {
        ...initialValues,
        logo: [
          {
            img: initialValues.logoUrl ?? '',
            background: initialValues.backgroundColor ?? backgrounds[0],
          },
        ],
        address: isEmptyAddress(initialValues.address) ? undefined : mapAddressToDadataArea(initialValues.address!),
        sportType: initialValues.sportType
          ? sportTypeToSelectOption(initialValues.sportType, t)
          : sportTypeToSelectOption(SportType.football, t),
      }
    : {
        name: '',
        sportType: sportTypeToSelectOption(SportType.football, t),
        phone: '',
        logo: [{ img: '', background: backgrounds[0] }],
      };

  const handleSubmit = useCallback(
    (values: OrganizerFormValues, formikHelpers: FormikHelpers<OrganizerFormValues>) => {
      const { address: changedAddress } = values;
      const address = changedAddress ? mapDadataAreaToAddress(changedAddress) : changedAddress;

      const data = {
        ...values,
        address,
        sportType: values.sportType.value,
      };

      onSubmit(data as OrganizerUpsertPayload, formikHelpers);
    },
    [onSubmit],
  );

  const { PreventNavigationModal } = usePreventNavigation({
    shouldPrevent: isFormDirty && !leavingConfirmed,
    onConfirm: () => {
      setLeavingConfirmed(true);
      setIsFormDirty(false);
    },
    defaultPath: Pages.CHAMPIONSHIPS.INDEX,
  });

  return (
    <Formik<OrganizerFormValues>
      enableReinitialize
      initialValues={initialFormValues}
      validationSchema={organizerFormValidator()}
      onSubmit={(values, formikHelpers) => {
        handleSubmit(values, formikHelpers);
        formikHelpers.resetForm({ values }); // делаем текущие значения базой
        setIsFormDirty(false);
      }}
      validateOnMount
    >
      {(formProps) => {
        setIsFormDirty(formProps.dirty);
        return (
          <form onSubmit={formProps.handleSubmit}>
            <div className="flex gap-6">
              <div className="w-1/2">
                <UDFormInput
                  disabled={isLoading}
                  name="name"
                  label="Название организации*"
                  placeholder="Введите название"
                  containerProps={{ className: 'mb-5' }}
                />

                <UDFormSelect
                  isDisabled={isLoading}
                  name="sportType"
                  label="Тип спорта"
                  placeholder="Выберите тип спорта"
                  isMulti={false}
                  options={sportTypeOptions}
                  containerProps={{ className: 'mb-5' }}
                />

                <UDFormInput
                  disabled={isLoading}
                  name="email"
                  label="Email"
                  placeholder="Email"
                  containerProps={{ className: 'mb-5' }}
                />

                <UDInputPhone
                  name="phone"
                  label="Телефон"
                  placeholder="Введите номер телефона"
                  wrapperProps={{ className: 'mb-6' }}
                  value={formProps.values.phone}
                />

                <UDFormAddressSelect
                  name="address"
                  label="Город"
                  placeholder={t`Введите адрес`}
                  containerProps={{ className: 'mb-5' }}
                  onlyCity={true}
                />

                <UDFormTextarea
                  disabled={isLoading}
                  name="description"
                  label="Описание"
                  placeholder="Введите текст"
                  containerProps={{ className: 'mb-5' }}
                />
              </div>
              <div className="w-1/2">
                <UDFormLogoField name="logo" label="Обложка и изображение" crop className="p-6" />
              </div>
            </div>
            <div className="mt-6 mb-2 flex justify-end">
              <UDButton
                variant="primary"
                type="submit"
                className="mb-3"
                disabled={isLoading || !formProps.dirty || !formProps.isValid}
                loading={isLoading || formProps.isSubmitting}
              >
                Сохранить
              </UDButton>
            </div>

            <PreventNavigationModal />
          </form>
        );
      }}
    </Formik>
  );
};
